import { ReactNode, useEffect } from "react";
import { Link, useLocation, Redirect } from "wouter";
import { useTranslation } from "react-i18next";
import { Home, ScanLine, Inbox as InboxIcon, User, WifiOff, RefreshCw } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth";
import { fetchAPI } from "@/lib/api";
import { useOnlineStatus, usePendingCount, drainQueue } from "@/lib/offline";
import { MobileInstallModal } from "@/components/pwa/MobileInstallPrompts";
import { canOpenPeopleAccess } from "@/components/people-access/model";
import { useCanAccessPeopleAccess } from "@/components/people-access/usePeopleAccess";

interface MobileLayoutProps {
  children: ReactNode;
}

export default function MobileLayout({ children }: MobileLayoutProps) {
  const { t } = useTranslation();
  const [location] = useLocation();
  const { user, roles, isLoading, currentWorkspace } = useAuth();
  const canEnterPeopleAccess = useCanAccessPeopleAccess();
  const online = useOnlineStatus();
  const pending = usePendingCount();
  const { data: inbox = [] } = useQuery<Array<{ type: string; id: string }>>({
    queryKey: ["/api/me/inbox"],
    queryFn: () => fetchAPI("/me/inbox"),
    enabled: !!user && !!currentWorkspace?.id && online,
    refetchInterval: 60_000,
  });
  const unreadCount = inbox.length;

  // Whenever the user returns online with pending items, kick off a drain.
  useEffect(() => {
    if (online && pending > 0) {
      void drainQueue();
    }
  }, [online, pending]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-400 text-sm">
        {t("mobilePages.loading")}
      </div>
    );
  }
  if (!user) {
    const next = encodeURIComponent(location || "/m");
    return <Redirect to={`/login?next=${next}`} />;
  }

  const inboxBadge = unreadCount && unreadCount > 0 ? String(unreadCount) : undefined;

  const showOfflineBanner = !online || pending > 0;

  return (
    <div className="min-h-screen bg-slate-50 font-sans">
      {showOfflineBanner && (
        <Link href="/m/profile/sync" data-testid="banner-offline">
          <div
            className={`fixed top-0 inset-x-0 z-50 flex items-center justify-between gap-2 px-3 text-[12px] font-medium ${
              !online
                ? "bg-amber-500 text-white"
                : "bg-slate-900 text-white"
            }`}
            style={{ paddingTop: "calc(env(safe-area-inset-top) + 4px)", paddingBottom: 4 }}
          >
            <span className="flex items-center gap-1.5 min-w-0">
              {!online ? (
                <>
                  <WifiOff className="w-3.5 h-3.5 shrink-0" />
                  <span className="truncate" data-testid="text-offline-label">Offline — work is saved locally</span>
                </>
              ) : (
                <>
                  <RefreshCw className="w-3.5 h-3.5 shrink-0" />
                  <span className="truncate">Syncing pending changes…</span>
                </>
              )}
            </span>
            {pending > 0 && (
              <span
                className="shrink-0 min-w-[18px] h-[18px] px-1.5 rounded-full bg-white/20 text-white text-[10px] font-bold flex items-center justify-center"
                data-testid="badge-pending-count"
              >
                {pending > 99 ? "99+" : pending}
              </span>
            )}
          </div>
        </Link>
      )}
      <div style={showOfflineBanner ? { paddingTop: "26px" } : undefined}>
        {(canOpenPeopleAccess(user, roles) || canEnterPeopleAccess) && !location.startsWith("/m/people-access") && (
          <details className="mx-3 my-2 rounded-xl border border-slate-200 bg-white">
            <summary className="min-h-11 cursor-pointer px-4 py-3 text-sm font-semibold">People &amp; access</summary>
            <div className="grid grid-cols-2 gap-1 px-2 pb-2">
              {[["people","People"],["roles","Roles"],["locations","Locations"],["sensitive-numbers","Sensitive numbers"],["compare","Compare access"]].map(([slug,label])=>(
                <Link key={slug} href={`/m/people-access/${slug}`} className="flex min-h-11 items-center rounded-lg px-3 text-sm text-blue-700 hover:bg-blue-50">{label}</Link>
              ))}
            </div>
          </details>
        )}
        {children}
      </div>
      <nav
        className="fixed bottom-0 inset-x-0 z-50 bg-white border-t border-slate-200 px-2 pt-2 flex justify-around"
        style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}
        data-testid="nav-mobile-bottom"
      >
        <Tab href="/m" label="Home" icon={<Home className="w-5 h-5" />} active={location === "/m" || location === "/m/"} testId="tab-home" />
        <Tab href="/m/scan" label="Scan" icon={<ScanLine className="w-6 h-6" />} highlight testId="tab-scan" />
        <Tab href="/m/inbox" label="Inbox" icon={<InboxIcon className="w-5 h-5" />} active={location.startsWith("/m/inbox")} badge={inboxBadge} testId="tab-inbox" />
        <Tab href="/m/profile" label="Profile" icon={<User className="w-5 h-5" />} active={location.startsWith("/m/profile")} testId="tab-profile" />
      </nav>
      <MobileInstallModal />
    </div>
  );
}

function Tab({
  href,
  label,
  icon,
  active,
  highlight,
  badge,
  testId,
}: {
  href: string;
  label: string;
  icon: ReactNode;
  active?: boolean;
  highlight?: boolean;
  badge?: string;
  testId: string;
}) {
  if (highlight) {
    return (
      <Link href={href} data-testid={testId}>
        <button className="-mt-6 w-14 h-14 rounded-full bg-gradient-to-br from-orange-500 to-amber-500 text-white shadow-lg flex flex-col items-center justify-center">
          {icon}
          <span className="text-[9px] mt-0.5 font-semibold">{label}</span>
        </button>
      </Link>
    );
  }
  return (
    <Link href={href} data-testid={testId}>
      <button
        className={`relative flex flex-col items-center gap-0.5 px-3 py-1 ${active ? "text-slate-900" : "text-slate-400"}`}
      >
        {icon}
        <span className="text-[10px] font-medium">{label}</span>
        {badge && (
          <span className="absolute top-0 end-1 min-w-[14px] h-[14px] px-1 rounded-full bg-orange-500 text-white text-[9px] font-bold flex items-center justify-center">
            {badge}
          </span>
        )}
      </button>
    </Link>
  );
}
